import type { EChartsOption } from 'echarts'
import { formatCompactNumber, isFiniteNumber, tooltipBase, tooltipHtml } from './format'
import { passThroughVisualMap } from './heatmapOption'
import { labelColorOn, sequentialColor, type ChartTheme } from './palette'

export interface CalendarDay {
  /** `YYYY-MM-DD`. */
  date: string
  value: number | null
}

export interface CalendarInput {
  days: CalendarDay[]
  /** Годы для отображения (малые кратные с одной шкалой); по умолчанию — все годы из данных. */
  years?: number[]
  valueFormat?: (value: number) => string
  /** Подпись значения в подсказке («заказов»). */
  valueLabel?: string
  /** Отметить дни без значения (например, «нет остатка») — их список. */
  markedDates?: string[]
  markedLabel?: string
}

export const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
export const WEEKDAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

const CELL = 14
const YEAR_HEIGHT = CELL * 7 + 40

/** Годы, встречающиеся в данных, по возрастанию. */
export function calendarYears(days: CalendarDay[]): number[] {
  const years = new Set<number>()
  for (const day of days) {
    const year = Number(String(day.date).slice(0, 4))
    if (Number.isInteger(year) && year > 1900) years.add(year)
  }
  return [...years].sort((a, b) => a - b)
}

export function calendarHeight(yearCount: number): number {
  return Math.max(1, yearCount) * YEAR_HEIGHT + 8
}

/** «12.03.2026, чт». */
export function formatCalendarDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, (month || 1) - 1, day || 1, 12)
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}, ${WEEKDAYS_SHORT[date.getDay()].toLowerCase()}`
}

/**
 * Календарь по дням: год — строка недель × 7 дней, неделя с понедельника. Цвет — последовательная шкала
 * одного оттенка, общая для всех лет (сравнимы между собой). Дни без данных — фон дорожки, не «ноль».
 */
export function buildCalendarOption(input: CalendarInput, theme: ChartTheme): EChartsOption {
  const years = input.years && input.years.length > 0 ? input.years : calendarYears(input.days)
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const numbers = input.days.map((day) => day.value).filter(isFiniteNumber)
  const max = numbers.length > 0 ? Math.max(...numbers) : 0
  const min = numbers.length > 0 ? Math.min(0, ...numbers) : 0
  const marked = new Set(input.markedDates ?? [])
  const byDate = new Map(input.days.map((day) => [day.date, day.value]))

  const calendars = years.map((year, index) => ({
    top: 24 + index * YEAR_HEIGHT,
    left: 40,
    right: 8,
    cellSize: ['auto', CELL] as [string, number],
    range: String(year),
    orient: 'horizontal' as const,
    splitLine: { show: false },
    itemStyle: { color: theme.track, borderColor: theme.surface, borderWidth: 2 },
    dayLabel: { firstDay: 1, nameMap: WEEKDAYS_SHORT, color: theme.textMuted, fontSize: 10, fontFamily: theme.fontFamily },
    monthLabel: { nameMap: MONTHS_SHORT, color: theme.textMuted, fontSize: 11, fontFamily: theme.fontFamily },
    yearLabel: { show: years.length > 1, position: 'left' as const, color: theme.textSecondary, fontSize: 12, fontFamily: theme.fontFamily, margin: 28 },
  }))

  const series = years.map((year, index) => ({
    type: 'heatmap' as const,
    coordinateSystem: 'calendar' as const,
    calendarIndex: index,
    cursor: 'pointer',
    data: input.days
      .filter((day) => day.date.startsWith(`${year}-`) && (isFiniteNumber(day.value) || marked.has(day.date)))
      .map((day) => {
        const fill = isFiniteNumber(day.value) ? sequentialColor(theme, day.value, min, max) : theme.track
        return {
          value: [day.date, isFiniteNumber(day.value) ? day.value : 0],
          itemStyle: marked.has(day.date)
            ? { color: fill, borderColor: theme.status.critical, borderWidth: 1 }
            : { color: fill },
          label: { color: labelColorOn(fill, theme) },
        }
      }),
  }))

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const date = String(params.value?.[0] ?? '')
        const value = byDate.get(date)
        return tooltipHtml(
          theme,
          formatCalendarDate(date),
          [{ label: input.valueLabel ?? 'значение', value: isFiniteNumber(value) ? valueFormat(value) : '—' }],
          marked.has(date) ? [input.markedLabel ?? 'Отмеченный день'] : undefined,
        )
      },
    },
    // Без visualMap ECharts в режиме разработки не рисует heatmap; карта ничего не перекрашивает (см. heatmapOption).
    visualMap: passThroughVisualMap(1, min, Math.max(max, min + 1)),
    calendar: calendars,
    series,
  } as EChartsOption
}
